import React, { useRef, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { quickConfig } from './config';
import { initializeUtils, runDevelopmentTests } from './utils';
import { API_MODULE_INFO, interceptorUtils, endpointUtils, errorUtils, responseUtils, requestUtils } from './modules/api/index';
import { UI_MODULE_INFO, PageContainer, ArbiWinHeader, ArbiWinFooter, MainContainer, HeroSection, Button } from './modules/ui/index';
import { TestSubscriptionPage, useSubscription } from './modules/subscription';
import { SurebetProvider, SurebetList, SurebetViewFilterBar, NearArbInfo, useSurebet, MaskedSurebetPreview } from './modules/surebets';
import { BestOddsProvider, BestOddsRadar, MaskedBestOddsPreview } from './modules/bestOdds';
import { FilterPanel } from './modules/filters';
import { CalculatorModal, CalculatorWrapper, openCalculatorFromSurebet } from './modules/calculator';
import { LandingPage, BridgePage, StaticPages, MarketingContent, EducationalContent, TelegramPromoSection } from './modules/public';
import { useTranslation } from './modules/i18n/hooks';
import { usePageTracking, logActivity, ACTIVITY_ACTIONS } from './modules/analytics';
import LoginPage from './modules/auth/components/LoginPage.jsx';
import { 
  RegisterPage, 
  ForgotPasswordPage, 
  ResetPasswordPage, 
  ProfilePage,
  ChangePasswordPage,
  useAuth,
  ProtectedRoute,
  PublicOnlyRoute,
  SuperAdminRoute,
  SubscriptionRoute,
  SessionConflictNotification
} from './modules/auth';
import { AdminActivityPage } from './modules/admin';
import SessionInvalidModal from './modules/auth/components/SessionInvalidModal';
import PromoBanner from './modules/subscription/components/PromoBanner';
import { TelegramDashboardBanner, NotificationCenter } from './modules/notifications';
import { ENV_CONFIG } from './config/environment';
import { StripeSubscriptionPage } from './modules/stripe';
import './modules/ui/styles/theme.css';
import './App.css';
import './modules/public/components/LandingPage.css';

/**
 * App - Main application component with routing structure
 * @returns {JSX.Element} Main app component
 */
function App() {
  const navigate = useNavigate();
  const { logout, isAuthenticated, user } = useAuth();
  const [isSessionInvalidModalOpen, setIsSessionInvalidModalOpen] = useState(false);

  usePageTracking();

  // Listen for session conflict/invalid events
  useEffect(() => {
    const handleSessionConflict = (event) => {
      console.log('🚨 App: Received session conflict event:', event.detail);
      
      // Check if tokens are already cleared (user might have logged out)
      const token = localStorage.getItem('arbiwin_auth_token');
      if (!token) {
        console.log('🚨 App: Tokens already cleared, ignoring session conflict event');
        return;
      }
      
      // Check if we're already on login page
      if (window.location.pathname === '/login') {
        console.log('🚨 App: Already on login page, ignoring session conflict event');
        return;
      }
      
      console.log('🚨 App: Setting modal to OPEN');
      setIsSessionInvalidModalOpen(true);
    };

    console.log('🔍 App: Setting up auth:sessionConflict event listener');
    window.addEventListener('auth:sessionConflict', handleSessionConflict);
    
    // Test event dispatch (development only)
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 App: Event listener registered. You can test with: window.dispatchEvent(new CustomEvent("auth:sessionConflict", {detail: {reason: "test"}}))');
    }
    
    return () => {
      console.log('🔍 App: Removing auth:sessionConflict event listener');
      window.removeEventListener('auth:sessionConflict', handleSessionConflict);
    };
  }, []);

  // Test configuration and utilities loading (development only) - run once
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
    console.log('🔧 Configuration loaded:', {
      apiBaseURL: quickConfig.apiBaseURL(),
      defaultLanguage: quickConfig.defaultLanguage(),
      primaryColor: quickConfig.primaryColor(),
      autoRefreshInterval: quickConfig.autoRefreshInterval(),
      isDevelopment: quickConfig.isDevelopment()
    });

    // Log API module information
    console.log('🌐 API Module loaded:', API_MODULE_INFO);

    // Log UI module information
    console.log('🎨 UI Module loaded:', UI_MODULE_INFO);

    // Log interceptor utilities
    console.log('🔧 Interceptor Utils:', {
      tokenExpired: interceptorUtils.token.isExpired(),
      hasToken: !!interceptorUtils.token.get(),
      performanceStats: interceptorUtils.performance.getStats()
    });

    // Test endpoint system
    console.log('🔗 Endpoint System Test:', {
      loginTest: endpointUtils.test('AUTH.LOGIN', {}, { email: 'test@example.com', password: 'password123' }),
      surebetTest: endpointUtils.test('SUREBET.MARKETS_ANALYSIS', {}, { bookmakers: 'betano,winbet', minMargin: 0.01 }),
      validationTest: endpointUtils.validateFilters({ minMargin: 0.02, bookmakers: ['betano', 'winbet'] })
    });

    // Test error handling system
    console.log('🚨 Error Handler System Test:', {
      stats: errorUtils.getStats(),
      networkErrorTest: errorUtils.handleNetwork(new Error('Network connection failed')),
      authErrorTest: errorUtils.handleAuth(new Error('Unauthorized')),
      subscriptionErrorTest: errorUtils.handleSubscription(new Error('Payment Required'))
    });

    // Test response processing system
    console.log('📊 Response Processing System Test:', {
      mockData: responseUtils.generateMock('marketsAnalysis', 3),
      cacheStats: responseUtils.getCacheStats(),
      performanceMetrics: responseUtils.getMetrics(),
      validationTest: responseUtils.validateMarketsAnalysis([{
        id: 1,
        event: { id: 1, date: '2024-01-15', time: '15:30', teamOne: 'Arsenal', teamTwo: 'Chelsea', championship: 'Premier League', sport: 'Football' },
        marginFlag: true,
        margins: { margin1X2: { value: 0.025, coefficients: [{ bookmaker: 'betano', type: 'Coef1', value: 2.1 }] } }
      }])
    });

    // Test request management system
    console.log('🚦 Request Management System Test:', {
      stats: requestUtils.getStats(),
      testExecution: requestUtils.execute(
        () => Promise.resolve({ test: 'data' }),
        { priority: 1, timeout: 5000, bypassRateLimit: true }
      ),
      configuration: {
        maxConcurrent: 6,
        enableRateLimiting: true,
        enableDeduplication: true,
        enablePriorityQueue: true
      }
    });

    // Initialize utilities
    initializeUtils({
      performance: true,
      accessibility: { runAudit: false }
    });

    // Run utility tests
    runDevelopmentTests();
    }
  }, []); // Empty dependency array to run only once
  const Landing = <LandingPage />;

  const Dashboard = () => {
    const { isAuthenticated, user, logout } = useAuth();
    const { t, language } = useTranslation();
    const subscription = useSubscription();
    const calculatorRef = useRef(null);
    const isActive = subscription?.computed?.isActive || false;
    const isLoadingSubscription = subscription?.loading?.status || false;
    const [activeProduct, setActiveProduct] = useState('arbitrage'); // 'arbitrage' | 'nearArb' | 'bestOdds' | 'notifications'
    const showNotificationTab = ENV_CONFIG.features.telegramNotifications;
    const nearArbEnabled = Boolean(subscription?.computed?.nearArbEnabled);

    useEffect(() => {
      if (activeProduct === 'nearArb' && !nearArbEnabled) {
        setActiveProduct('arbitrage');
      }
    }, [activeProduct, nearArbEnabled]);
    
    // Debug subscription status
    console.log('🔍 Dashboard: Subscription status:', {
      isAuthenticated,
      subscription: subscription?.status,
      isActive,
      isLoadingSubscription,
      computed: subscription?.computed
    });
    
    const handleLogout = async () => {
      console.log('🔍 Dashboard: Logout button clicked');
      try {
        await logout();
        console.log('🔍 Dashboard: Logout completed, navigating to home');
        navigate('/');
      } catch (error) {
        console.error('🔍 Dashboard: Logout error:', error);
      }
    };

    const handleCalculatorClick = (surebet, marginKey) => {
      logActivity(ACTIVITY_ACTIONS.CALC_SUREBET_OPEN);
      openCalculatorFromSurebet(calculatorRef, surebet, marginKey);
    };

    const handleProductTabClick = (product) => {
      if (product === activeProduct) {
        return;
      }
      setActiveProduct(product);
      if (product === 'arbitrage') {
        logActivity(ACTIVITY_ACTIONS.TAB_ARBITRAGE);
      } else if (product === 'bestOdds') {
        logActivity(ACTIVITY_ACTIONS.TAB_BEST_ODDS);
      } else if (product === 'notifications') {
        logActivity(ACTIVITY_ACTIONS.TAB_NOTIFICATIONS);
      }
    };
    
    // Show loading while subscription status is being determined
    if (isLoadingSubscription) {
      return (
        <PageContainer>
          <CalculatorWrapper calculatorRef={calculatorRef} />
          <ArbiWinHeader
            isAuthenticated={isAuthenticated}
            user={user}
            onLogin={() => navigate('/login')}
            onRegister={() => navigate('/register')}
            onLogout={handleLogout}
            onProfile={() => navigate('/profile')}
            onHomeClick={() => navigate('/')}
          />
          <MainContainer>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <p>Loading subscription status...</p>
            </div>
          </MainContainer>
          <ArbiWinFooter />
        </PageContainer>
      );
    }
    
    // If user doesn't have active subscription, show dashboard with masked previews only
    // The backend /masked endpoint handles all masking automatically
    if (!isActive) {
      console.log('🔍 Dashboard: Rendering masked preview for non-active subscription');
      return (
        <PageContainer>
          <CalculatorWrapper calculatorRef={calculatorRef} />
          <ArbiWinHeader
            isAuthenticated={isAuthenticated}
            user={user}
            onLogin={() => navigate('/login')}
            onRegister={() => navigate('/register')}
            onLogout={handleLogout}
            onProfile={() => navigate('/profile')}
            onHomeClick={() => navigate('/')}
          />

          <PromoBanner />
          
          <MainContainer>
            {/* Product toggle - Arbitrage Analysis vs Best Odds Radar (masked previews, same as public landing) */}
            <div
              className="landing-hero__product-toggle dashboard-masked-preview__toggle"
              role="group"
              aria-label={t('landing.heroToggle.ariaLabel', 'Превключи между ArbiWin продуктите')}
            >
              <button
                type="button"
                className={`landing-hero__product-toggle-btn ${activeProduct === 'arbitrage' ? 'landing-hero__product-toggle-btn--active' : ''}`}
                aria-pressed={activeProduct === 'arbitrage'}
                onClick={() => setActiveProduct('arbitrage')}
              >
                {t('landing.heroToggle.arbitrage')}
              </button>
              <button
                type="button"
                className={`landing-hero__product-toggle-btn ${activeProduct === 'bestOdds' ? 'landing-hero__product-toggle-btn--active' : ''}`}
                aria-pressed={activeProduct === 'bestOdds'}
                onClick={() => setActiveProduct('bestOdds')}
              >
                {t('landing.heroToggle.bestOdds')}
              </button>
            </div>

            {activeProduct === 'arbitrage' && (
              <p className="dashboard-product-info">
                {t('dashboard.productInfo.arbitrage')}
              </p>
            )}

            {/* Filter panel in inactive mode (informative only, Arbitrage only) */}
            {activeProduct === 'arbitrage' && (
              <FilterPanel
                inactive={true}
                showRefreshButton={false}
                collapsible={true}
              />
            )}
            
            {/* Show only the masked previews section with real data from the /masked endpoints */}
            <section className="dashboard-masked-preview">
              {activeProduct === 'arbitrage' ? (
                <MaskedSurebetPreview
                  key={`masked-preview-${isAuthenticated ? 'authenticated' : 'public'}`}
                  onActivate={() => navigate('/subscription')}
                  onCalculatorClick={handleCalculatorClick}
                  showHeader={true}
                  maxItems={5}
                />
              ) : (
                <MaskedBestOddsPreview
                  key={`masked-best-odds-preview-${isAuthenticated ? 'authenticated' : 'public'}`}
                  onActivate={() => navigate('/subscription')}
                  showHeader={true}
                  maxItems={3}
                />
              )}
            </section>

            <TelegramPromoSection onCtaClick={() => navigate('/subscription')} />

            {/* Testimonials / Social Proof Section */}
            <section className="landing-testimonials">
              <div className="landing-testimonials__container">
                <h2 className="landing-testimonials__title">{t('landing.testimonials.title')}</h2>
                <p className="landing-testimonials__subtitle">
                  {t('landing.testimonials.subtitle')}
                </p>

                <div className="landing-testimonials__grid">
                  <div className="landing-testimonial-card">
                    <div className="landing-testimonial-card__content">
                      <div className="landing-testimonial-card__quote">
                        {t('landing.testimonials.testimonial1.quote')}
                      </div>
                    </div>
                  </div>

                  <div className="landing-testimonial-card">
                    <div className="landing-testimonial-card__content">
                      <div className="landing-testimonial-card__quote">
                        {t('landing.testimonials.testimonial2.quote')}
                      </div>
                    </div>
                  </div>

                  <div className="landing-testimonial-card">
                    <div className="landing-testimonial-card__content">
                      <div className="landing-testimonial-card__quote">
                        {t('landing.testimonials.testimonial3.quote')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="landing-testimonials__trust">
                  <div className="landing-trust-item">
                    <div className="landing-trust-item__value">{t('landing.testimonials.trust.users')}</div>
                    <div className="landing-trust-item__label">{t('landing.testimonials.trust.usersLabel')}</div>
                  </div>
                  <div className="landing-trust-item">
                    <div className="landing-trust-item__value">{t('landing.testimonials.trust.surebets')}</div>
                    <div className="landing-trust-item__label">{t('landing.testimonials.trust.surebetsLabel')}</div>
                  </div>
                  <div className="landing-trust-item">
                    <div className="landing-trust-item__value">{t('landing.testimonials.trust.satisfaction')}</div>
                    <div className="landing-trust-item__label">{t('landing.testimonials.trust.satisfactionLabel')}</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Marketing Content Section */}
            <section className="landing-marketing">
              <MarketingContent
                onRegisterClick={() => navigate('/subscription')}
              />
            </section>

            {/* Pricing Section */}
            <section className="landing-pricing">
              <div className="landing-pricing__container">
                <h2 className="landing-pricing__title">{t('landing.pricing.title')}</h2>
                {t('landing.pricing.description') && (
                  <p className="landing-pricing__description">
                    {t('landing.pricing.description')}
                  </p>
                )}

                <div className="landing-pricing__card">
                  <div className="landing-pricing__price">
                    <span className="landing-pricing__price-amount">
                      €17.99
                    </span>
                    <span className="landing-pricing__price-period">{t('landing.pricing.period')}</span>
                  </div>

                  <div className="landing-pricing__promo">
                    <p className="landing-pricing__promo-text">{t('landing.pricing.promoText')}</p>
                    <p className="landing-pricing__promo-disclaimer">{t('landing.pricing.promoDisclaimer')}</p>
                  </div>

                  <h4 className="landing-pricing__features-heading">{t('landing.pricing.featuresIncludes')}</h4>
                  <ul className="landing-pricing__features">
                    <li>{t('landing.pricing.feature1')}</li>
                    <li>{t('landing.pricing.feature2')}</li>
                    <li>{t('landing.pricing.feature3')}</li>
                    <li>{t('landing.pricing.feature4')}</li>
                    <li>{t('landing.pricing.feature5')}</li>
                  </ul>

                  <Button
                    variant="primary"
                    size="large"
                    onClick={() => navigate('/subscription')}
                    className="landing-pricing__cta"
                  >
                    {t('landing.pricing.cta')}
                  </Button>
                </div>
              </div>
            </section>

            {/* Educational Content Section */}
            <section className="landing-educational">
              <EducationalContent
                onRegisterClick={() => navigate('/subscription')}
              />
            </section>

            {/* CTA Section */}
            <section className="landing-cta">
              <div className="landing-cta__container">
                <h2 className="landing-cta__title">{t('landing.cta.title')}</h2>
                <Button
                  variant="primary"
                  size="large"
                  onClick={() => navigate('/subscription')}
                >
                  {t('landing.cta.button')}
                </Button>
              </div>
            </section>
          </MainContainer>
          
          <ArbiWinFooter />
          
        {/* Calculator Modal - SIMPLIFIED VERSION FOR TESTING */}
        <CalculatorModal />
        </PageContainer>
      );
    }

    // If user has active subscription, show full surebet list
    console.log('🔍 Dashboard: Rendering full surebet list for active subscription');
    return (
      <PageContainer>
        <CalculatorWrapper calculatorRef={calculatorRef} />
        <ArbiWinHeader
          isAuthenticated={isAuthenticated}
          user={user}
          onLogin={() => navigate('/login')}
          onRegister={() => navigate('/register')}
          onLogout={handleLogout}
          onProfile={() => navigate('/profile')}
          onHomeClick={() => navigate('/')}
        />
        <TelegramDashboardBanner />
        <MainContainer>
          {/* Product tab bar - Arbitrage Analysis vs Best Odds Radar (independent products) */}
          <div className="dashboard-product-tabs" role="tablist" aria-label={t('landing.heroToggle.ariaLabel')}>
            <button
              type="button"
              role="tab"
              aria-selected={activeProduct === 'arbitrage'}
              className={`dashboard-product-tabs__tab ${activeProduct === 'arbitrage' ? 'dashboard-product-tabs__tab--active' : ''}`}
              onClick={() => handleProductTabClick('arbitrage')}
            >
              {t('dashboard.tabs.arbitrage')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeProduct === 'bestOdds'}
              className={`dashboard-product-tabs__tab ${activeProduct === 'bestOdds' ? 'dashboard-product-tabs__tab--active' : ''}`}
              onClick={() => handleProductTabClick('bestOdds')}
            >
              {t('dashboard.tabs.bestOdds')}
            </button>
            {nearArbEnabled && (
              <button
                type="button"
                role="tab"
                aria-selected={activeProduct === 'nearArb'}
                className={`dashboard-product-tabs__tab ${activeProduct === 'nearArb' ? 'dashboard-product-tabs__tab--active' : ''}`}
                onClick={() => handleProductTabClick('nearArb')}
              >
                {t('dashboard.tabs.nearArb')}
              </button>
            )}
            {showNotificationTab && (
              <button
                type="button"
                role="tab"
                aria-selected={activeProduct === 'notifications'}
                className={`dashboard-product-tabs__tab ${activeProduct === 'notifications' ? 'dashboard-product-tabs__tab--active' : ''}`}
                onClick={() => handleProductTabClick('notifications')}
              >
                {t('dashboard.tabs.notifications')}
              </button>
            )}
          </div>

          {activeProduct !== 'notifications' && (
            <p className="dashboard-product-info">
              {activeProduct === 'arbitrage'
                ? t('dashboard.productInfo.arbitrage')
                : activeProduct === 'nearArb'
                  ? t('dashboard.productInfo.nearArb')
                  : t('dashboard.productInfo.bestOdds')}
            </p>
          )}

          {activeProduct === 'arbitrage' ? (
            <SurebetProvider key="arbitrage">
              {/* Filter panel in active mode (functional) */}
              <FilterPanel
                inactive={false}
                showRefreshButton={true}
                collapsible={true}
                onFiltersApplied={() => console.log('✅ Filters applied successfully')}
                onFiltersReset={() => console.log('✅ Filters reset successfully')}
              />

              <SurebetViewFilterBar />
              
              <SurebetList
                showRefresh={false}  // FilterPanel handles refresh now
                showStats={true}     // Always show statistics
                showFilters={false}
                onCalculatorClick={handleCalculatorClick}
              />
            </SurebetProvider>
          ) : activeProduct === 'nearArb' ? (
            <SurebetProvider key="nearArb" mode="nearArb">
              <NearArbInfo />
              <SurebetViewFilterBar />
              <SurebetList
                showRefresh={true}
                showStats={false}
                showFilters={false}
                onCalculatorClick={handleCalculatorClick}
              />
            </SurebetProvider>
          ) : activeProduct === 'bestOdds' ? (
            <BestOddsProvider>
              <BestOddsRadar showStats={true} showRefresh={true} />
            </BestOddsProvider>
          ) : (
            <NotificationCenter />
          )}
        </MainContainer>
        <ArbiWinFooter />
        
        {/* Calculator Modal - SIMPLIFIED VERSION FOR TESTING */}
        <CalculatorModal />
      </PageContainer>
    );
  };

  return (
    <>
      <SessionConflictNotification />
      <SessionInvalidModal 
        isOpen={isSessionInvalidModalOpen}
        onClose={() => setIsSessionInvalidModalOpen(false)}
      />
      <Routes>
      {/* Public routes - only accessible when not authenticated */}
      <Route path="/" element={
        <PublicOnlyRoute>
          {Landing}
        </PublicOnlyRoute>
      } />
      <Route path="/login" element={
        <PublicOnlyRoute>
          <LoginPage />
        </PublicOnlyRoute>
      } />
      <Route path="/register" element={
        <PublicOnlyRoute>
          <RegisterPage />
        </PublicOnlyRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicOnlyRoute>
          <ForgotPasswordPage />
        </PublicOnlyRoute>
      } />
      <Route path="/reset-password" element={
        <PublicOnlyRoute>
          <ResetPasswordPage />
        </PublicOnlyRoute>
      } />
      <Route path="/reset-password/:token" element={
        <PublicOnlyRoute>
          <ResetPasswordPage />
        </PublicOnlyRoute>
      } />
      
      {/* Hyper-Stealth Google Ads landing page - accessible to everyone (no auth redirect) */}
      <Route path="/data-analytics" element={<BridgePage />} />

      {/* Static pages - accessible to all users (authenticated and unauthenticated) */}
      <Route path="/static/:page" element={
        <StaticPages
          isAuthenticated={isAuthenticated}
          user={user}
          onLogout={async () => {
            try {
              await logout();
              navigate('/');
            } catch (error) {
              console.error('Logout error:', error);
            }
          }}
          onProfile={() => navigate('/profile')}
        />
      } />
      
      {/* Protected routes - require authentication */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage 
            onHomeClick={() => navigate('/')}
            onLogin={() => navigate('/login')}
            onRegister={() => navigate('/register')}
            onLogout={async () => {
              try {
                await logout();
                navigate('/');
              } catch (error) {
                console.error('Logout error:', error);
              }
            }}
          />
        </ProtectedRoute>
      } />
      
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      
      {/* Subscription page - for users without active subscription */}
      <Route path="/subscription" element={
        <ProtectedRoute>
          <StripeSubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Stripe checkout success redirect */}
      <Route path="/subscription/success" element={
        <ProtectedRoute>
          <StripeSubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Stripe checkout cancel redirect */}
      <Route path="/subscription/cancel" element={
        <ProtectedRoute>
          <StripeSubscriptionPage />
        </ProtectedRoute>
      } />
      
      {/* Admin routes - Super Admin activity summary */}
      <Route path="/admin" element={<Navigate to="/admin/activity" replace />} />
      <Route path="/admin/activity" element={
        <SuperAdminRoute>
          <AdminActivityPage />
        </SuperAdminRoute>
      } />
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}



export default App;
